import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Grid, Card, Text, Group, Stack,
  Alert, Badge, Loader, Center, ThemeIcon,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconLayoutDashboard, IconAlertTriangle,
  IconTrendingUp, IconTrendingDown, IconTarget,
  IconCar, IconCoin, IconFileText,
} from '@tabler/icons-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer
} from 'recharts';
import { notifications } from '@mantine/notifications';

const MESE_CORRENTE = (() => {
  const now = new Date();
  // Fine periodo = mese precedente (escludi mese corrente)
  const fine = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  // Inizio periodo = 12 mesi prima della fine
  const da = new Date(fine.getFullYear(), fine.getMonth() - 11, 1);
  const daStr = `${da.getFullYear()}-${String(da.getMonth() + 1).padStart(2, '0')}-01`;
  const aStr = `${fine.getFullYear()}-${String(fine.getMonth() + 1).padStart(2, '0')}-01`;
  const daLabel = da.toLocaleString('it-IT', { month: 'short' }).toUpperCase();
  const aLabel = fine.toLocaleString('it-IT', { month: 'short' }).toUpperCase();
  return { da: daStr, a: aStr, label: `${daLabel} ${da.getFullYear()} – ${aLabel} ${fine.getFullYear()}` };
})();

export default function Dashboard() {
  const [cdg, setCdg] = useState(null);
  const [pratiche, setPratiche] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    Promise.all([
      api.get('/cdg/dashboard', { params: { mese_da: MESE_CORRENTE.da, mese_a: MESE_CORRENTE.a } }),
      api.get('/pratiche-auto'),
    ]).then(([r1, r2]) => {
      setCdg(r1.data);
      setPratiche(r2.data);
    }).catch(() => {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare i dati', color: 'red' });
    }).finally(() => setLoading(false));
  }, []);

  const fmt = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  if (loading) return <Center h={400}><Loader size="lg" color="teal" /></Center>;
  if (!cdg) return (
    <Alert icon={<IconAlertTriangle size={16} />} color="red">Errore caricamento dati</Alert>
  );

  const getArea = (key) => cdg.aree.find(a => a.area === key) || { ricavi: 0, costi_totali: 0, margine: 0, margine_perc: 0 };
  const fco = getArea('usave_fco');
  const cia = getArea('usave_cia');
  const comp = getArea('commercio_veicoli');
  const nlt = getArea('consulenza_nlt');

  const macroAree = [
    { label: 'U-Save Fiumicino', sub: 'FCO', d: fco, color: '#205357', icon: IconCoin },
    { label: 'U-Save Ciampino', sub: 'CIA', d: cia, color: '#1a6b5a', icon: IconCoin },
    { label: 'Compravendita Auto', sub: 'Commercio Veicoli', d: comp, color: '#339fa3', icon: IconCar },
    { label: 'Consulenza NLT', sub: 'Noleggio Lungo Termine', d: nlt, color: '#66b7ba', icon: IconFileText },
  ];

  const barData = macroAree.map(({ label, d }) => ({
    name: label,
    Ricavi: d.ricavi,
    Costi: d.costi_totali,
    Margine: d.margine,
  }));

  const pratiche_attive = pratiche
    ? pratiche.filter(p => !['chiusa', 'annullata'].includes(p.stato)).length
    : 0;

  return (
    <Stack gap="lg">
      <Group>
        <IconLayoutDashboard size={28} color="rgb(32, 83, 87)" />
        <Title order={2}>Dashboard KPI — {MESE_CORRENTE.label}</Title>
      </Group>

      {/* KPI 3 Aree */}
      <Grid>
        {macroAree.map(({ label, sub, d, color, icon: Icon }) => (
          <Grid.Col key={label} span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding={isMobile ? 'sm' : 'lg'} radius="md" withBorder>
              <Group mb={4} gap={isMobile ? 6 : 'sm'}>
                {!isMobile && <ThemeIcon color="teal" variant="light" size="lg"><Icon size={20} /></ThemeIcon>}
                <div>
                  <Text fw={700} size={isMobile ? 'xs' : 'sm'}>{label}</Text>
                  <Text size="xs" c="dimmed">{sub}</Text>
                </div>
              </Group>
              <Stack gap={4} mt="sm">
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Ricavi</Text>
                  <Text size="xs" fw={600} c="green">{fmt(d.ricavi)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Costi</Text>
                  <Text size="xs" fw={600} c="red">{fmt(d.costi_totali)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Margine</Text>
                  <Text size="xs" fw={700} c={d.margine >= 0 ? 'green' : 'red'}>{fmt(d.margine)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Margine %</Text>
                  <Badge
                    color={d.margine_perc >= 10 ? 'green' : d.margine_perc >= 0 ? 'yellow' : 'red'}
                    variant="light" size="xs"
                  >
                    {d.margine_perc}%
                  </Badge>
                </Group>
              </Stack>
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {/* Riepilogo aziendale + grafico */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="sm">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">Ricavi Totali</Text>
                <ThemeIcon color="green" variant="light" size="lg"><IconTrendingUp size={20} /></ThemeIcon>
              </Group>
              <Text size="xl" fw={700} c="green">{fmt(cdg.riepilogo.totale_ricavi)}</Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">Costi Totali</Text>
                <ThemeIcon color="red" variant="light" size="lg"><IconTrendingDown size={20} /></ThemeIcon>
              </Group>
              <Text size="xl" fw={700} c="red">{fmt(cdg.riepilogo.totale_costi)}</Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">Margine Netto</Text>
                <ThemeIcon color={cdg.riepilogo.margine >= 0 ? 'green' : 'red'} variant="light" size="lg">
                  <IconTarget size={20} />
                </ThemeIcon>
              </Group>
              <Text size="xl" fw={700} c={cdg.riepilogo.margine >= 0 ? 'green' : 'red'}>
                {fmt(cdg.riepilogo.margine)}
              </Text>
              <Badge
                color={cdg.riepilogo.margine_perc >= 10 ? 'green' : cdg.riepilogo.margine_perc >= 0 ? 'yellow' : 'red'}
                variant="light" mt={6}
              >
                {cdg.riepilogo.margine_perc}%
              </Badge>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder ta="center">
              <ThemeIcon color="teal" variant="light" size={50} radius="xl" mx="auto" mb="xs">
                <IconCar size={26} />
              </ThemeIcon>
              <Text size="xl" fw={700} c="teal">{pratiche_attive}</Text>
              <Text size="sm" c="dimmed">Pratiche Auto Attive</Text>
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
            <Text fw={600} mb="md">Ricavi vs Costi vs Margine per Area</Text>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={barData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <ReTooltip formatter={(v) => fmt(v)} />
                <Legend />
                <Bar dataKey="Ricavi" fill="#00C49F" />
                <Bar dataKey="Costi" fill="#FF8042" />
                <Bar dataKey="Margine" fill="#0088FE" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
